import ConfirmDialog from '@/components/ui/ConfirmDialog';
import React, { useEffect, useState, useRef } from 'react';
import { KeyRound, Plus, Trash2, Copy, Check, Plug } from 'lucide-react';
import { base44 } from '@/api/base44Client';

const SCOPES = [
  ['read:status', 'Read status & scores'], ['read:assessment', 'Read assessment'], ['write:assessment', 'Answer questions'],
  ['read:tasks', 'Read roadmap'], ['write:tasks', 'Create/update tasks'], ['read:evidence', 'Read evidence'], ['write:evidence', 'Upload evidence'],
  ['ai:ask', 'Ask Dai'], ['ai:personas', 'Personas & focus groups'], ['ai:generate', 'Generate documents'], ['ai:analyse', 'Analyse evidence documents'],
];
async function api(method, path, body) {
  const r = await base44.tenant.request(method, path, body);
  const j = await r.json().catch(() => ({}));
  if (!r.ok) throw new Error(j.error || `HTTP ${r.status}`);
  return j;
}

export default function IntegrationsCard({ orgName }) {
  const [keys, setKeys] = useState([]);
  const [revokePrompt, setRevokePrompt] = useState(null);
  const revokeReturnRef = useRef(null);
  const headingRef = useRef(null);
  const [creating, setCreating] = useState(false);
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState(['read:status', 'read:assessment', 'read:tasks', 'read:evidence', 'ai:ask']);
  const [newKey, setNewKey] = useState(null);
  const [copied, setCopied] = useState('');
  const [error, setError] = useState('');
  const mcpUrl = `${window.location.origin}/mcp`;

  const load = () => api('GET', '/keys').then(setKeys).catch((e) => setError(e.message));
  useEffect(() => { load(); }, []);

  async function create() {
    setError(''); setCreating(true);
    try { const k = await api('POST', '/keys', { name: name || 'API key', scopes }); setNewKey(k); setName(''); await load(); }
    catch (e) { setError(e.message); } finally { setCreating(false); }
  }
  async function revoke(id) { await api('DELETE', `/keys/${id}`); await load(); revokeReturnRef.current = headingRef.current; }
  const copy = async (text, tag) => { try { await navigator.clipboard.writeText(text); setCopied(tag); setTimeout(() => setCopied(''), 1500); } catch {} };

  return (
    <div className="mt-6 bg-white border border-border rounded-2xl p-6 shadow-sm">
      {revokePrompt && <ConfirmDialog title={`Revoke ${revokePrompt.name}?`} description="Anything using this key will stop working immediately." action="Revoke key" returnFocusRef={revokeReturnRef} onClose={() => setRevokePrompt(null)} onConfirm={() => revoke(revokePrompt.id)} />}
      <div className="flex items-center gap-2 mb-1"><Plug className="w-5 h-5 text-teal-600" aria-hidden="true" /><h2 ref={headingRef} tabIndex={-1} className="text-lg font-bold text-[#0F1C2E]">Integrations — API & AI agents</h2></div>
      <p className="text-sm text-muted-foreground mb-5">Connect Copilot, Claude, ChatGPT or your own systems to {orgName || 'your organisation'}. Keys are scoped, rate-limited and every call is logged.</p>

      <div className="rounded-xl bg-slate-50 border border-slate-200 p-4 mb-5">
        <p className="text-xs font-semibold uppercase tracking-wide text-slate-500 mb-1">MCP endpoint</p>
        <div className="flex items-center gap-2"><code className="text-sm break-all">{mcpUrl}</code><button onClick={() => copy(mcpUrl, 'mcp')} className="p-1.5 rounded hover:bg-slate-200" aria-label="Copy MCP URL">{copied === 'mcp' ? <Check className="w-4 h-4 text-teal-600" /> : <Copy className="w-4 h-4" />}</button></div>
        <p className="text-xs text-slate-500 mt-2">Add it as a custom connector with header <code>Authorization: Bearer &lt;your key&gt;</code>. Claude Code: <code>claude mcp add --transport http inclusivecore {mcpUrl} --header "Authorization: Bearer …"</code></p>
      </div>

      {newKey && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 mb-5" role="alert">
          <p className="text-sm font-semibold text-amber-800 mb-1">Copy your new key now — it is shown once.</p>
          <div className="flex items-center gap-2"><code className="text-sm break-all">{newKey.key}</code><button onClick={() => copy(newKey.key, 'key')} className="p-1.5 rounded hover:bg-amber-100" aria-label="Copy API key">{copied === 'key' ? <Check className="w-4 h-4 text-teal-600" /> : <Copy className="w-4 h-4" />}</button></div>
          <button onClick={() => setNewKey(null)} className="text-xs text-amber-700 underline mt-2">I've saved it</button>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-4 mb-5">
        <div>
          <label className="block text-sm font-medium mb-1" htmlFor="key-name">Key name</label>
          <input id="key-name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Copilot — HR team" className="w-full px-3 py-2 rounded-lg border border-border text-sm" />
        </div>
        <fieldset>
          <legend className="block text-sm font-medium mb-1">Scopes</legend>
          <div className="grid grid-cols-2 gap-1">
            {SCOPES.map(([s, label]) => (
              <label key={s} className="flex items-center gap-2 text-xs"><input type="checkbox" checked={scopes.includes(s)} onChange={(e) => setScopes((cur) => (e.target.checked ? [...cur, s] : cur.filter((x) => x !== s)))} />{label}</label>
            ))}
          </div>
        </fieldset>
      </div>
      <button onClick={create} disabled={creating || scopes.length === 0} className="flex items-center gap-2 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm font-semibold hover:bg-teal-700 disabled:opacity-50"><Plus className="w-4 h-4" aria-hidden="true" />{creating ? 'Creating…' : 'Create key'}</button>
      {error && <p className="text-sm text-destructive mt-3" role="alert">{error}</p>}

      <div className="mt-6">
        <h3 className="text-sm font-semibold mb-2 flex items-center gap-2"><KeyRound className="w-4 h-4" aria-hidden="true" />Active keys ({keys.length})</h3>
        {keys.length === 0 ? <p className="text-sm text-muted-foreground">No keys yet.</p> : (
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs uppercase text-muted-foreground"><th className="py-1">Name</th><th>Prefix</th><th>Scopes</th><th>Last used</th><th></th></tr></thead>
            <tbody>{keys.map((k) => (
              <tr key={k.id} className="border-t border-border"><td className="py-2 font-medium">{k.name}</td><td><code>{k.start}…</code></td><td className="text-xs">{(k.scopes || []).join(', ')}</td><td className="text-xs">{k.lastRequest ? new Date(k.lastRequest).toLocaleString('en-GB') : 'never'}</td>
                <td className="text-right"><button onClick={event => { revokeReturnRef.current = event.currentTarget; setRevokePrompt(k); }} className="p-1.5 rounded text-destructive hover:bg-red-50" aria-label={`Revoke ${k.name}`}><Trash2 className="w-4 h-4" /></button></td></tr>
            ))}</tbody>
          </table>
        )}
      </div>
    </div>
  );
}
